import { useEffect, useState } from 'react'
import { supabase } from '../lib/supabase.js'
import { subscribeBrowserResume } from '../lib/marketData/browserResume.js'
import { reconcileSetupEntryStatus, setupEntryRefs } from '../lib/setupEntryStatus.js'

/** One read batch for both desktop/mobile cards. Never writes setup or broker state. */
export function useSetupEntryStatus({ projects, userId, accessToken, notifications = [] }) {
  const refsKey = setupEntryRefs(projects).join(',')
  const scope = JSON.stringify([userId, refsKey])
  const [evidence, setEvidence] = useState(null)
  const notificationKey = JSON.stringify(notifications.filter(row => row?.serverBacked)
    .flatMap(row => row.sourceNotificationIds?.length ? row.sourceNotificationIds : [row.id]).filter(Boolean).sort())

  useEffect(() => {
    if (!userId || !accessToken || !refsKey) return undefined
    let disposed = false
    let running = false
    let queued = false
    let controller
    let rejectedToken = null
    const refresh = async () => {
      if (disposed) return
      if (running) { queued = true; return }
      running = true
      controller = new AbortController()
      const timeout = setTimeout(() => controller.abort(), 10_000)
      try {
        // AuthProvider deliberately keeps its context session stable on refresh.
        // Resolve credentials at dispatch, and never send another user's token
        // through a poller that is still cleaning up after an account change.
        const { data, error } = await supabase.auth.getSession()
        const currentSession = data?.session
        const token = currentSession?.access_token
        if (disposed || controller.signal.aborted || error || currentSession?.user?.id !== userId || !token || token === rejectedToken) return
        const params = new URLSearchParams({ orderRefs: refsKey, includeTerminal: '1' })
        const response = await fetch(`/api/schwab/orders?${params}`, {
          headers: { Authorization: `Bearer ${token}` }, signal: controller.signal,
        })
        if (response.status === 401) rejectedToken = token
        if (!response.ok) return
        const body = await response.json()
        if (disposed || !Array.isArray(body?.orders)) return
        setEvidence(previous => {
          // The endpoint can omit an account/ref after a failed direct read.
          // Retain its last broker-confirmed evidence until it is returned again.
          const rows = new Map((previous?.scope === scope ? previous.orders : [])
            .map(row => [`${row.accountNumber}:${row.orderId}`, row]))
          for (const row of body.orders) rows.set(`${row.accountNumber}:${row.orderId}`, row)
          return { scope, orders: [...rows.values()] }
        })
      } catch { /* failed reads preserve the last confirmed card */ }
      finally {
        clearTimeout(timeout)
        running = false
        if (queued && !disposed) { queued = false; void refresh() }
      }
    }
    void refresh()
    const unsubscribe = subscribeBrowserResume(refresh)
    window.addEventListener('erevis:schwab-sync-complete', refresh)
    const timer = setInterval(() => {
      if (document.visibilityState !== 'hidden') void refresh()
    }, 30_000)
    return () => {
      disposed = true
      controller?.abort()
      clearInterval(timer)
      unsubscribe()
      window.removeEventListener('erevis:schwab-sync-complete', refresh)
    }
  }, [userId, accessToken, refsKey, scope, notificationKey])

  const orders = userId && accessToken && evidence?.scope === scope ? evidence.orders : []
  return new Map(projects.map(project => [project.id, reconcileSetupEntryStatus(project, orders)]))
}
